import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Building2, MapPin, Home } from "lucide-react";
import { cities } from "../../data/citiesData";

// Helper function to normalize location strings for better matching
const normalizeLocationString = (str) => {
  if (!str) return "";
  return str
    .toLowerCase()
    .trim()
    .replace(/\s+/g, '') // Remove all spaces
    .replace(/[-_]/g, '') // Remove hyphens and underscores
    .replace(/[()]/g, '') // Remove parentheses
    .replace(/\./g, ''); // Remove dots
};

const HeroSection = () => {
  const [propertyType, setPropertyType] = useState("");
  const [city, setCity] = useState("");
  const [location, setLocation] = useState("");
  const [listingType, setListingType] = useState("sale");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");

  const navigate = useNavigate();

  // Pakistani property types with local terminologies
  const propertyTypeOptions = [
    { value: "lower-portion", label: "Lower Portion", group: "Residential" },
    { value: "upper-portion", label: "Upper Portion", group: "Residential" },
    { value: "full-house", label: "Full House", group: "Residential" },
    { value: "farmhouse", label: "Farmhouse", group: "Residential" },
    { value: "banglow", label: "Banglow", group: "Residential" },
    { value: "apartment", label: "Apartment", group: "Residential" },
    { value: "residential-plot", label: "Residential Plot", group: "Plots & Land" },
    { value: "commercial-plot", label: "Commercial Plot", group: "Plots & Land" },
    { value: "agricultural-land", label: "Agricultural Land", group: "Plots & Land" },
    { value: "shop", label: "Shop", group: "Commercial" },
    { value: "office", label: "Office", group: "Commercial" },
    { value: "godown", label: "Godown/Warehouse", group: "Commercial" },
    { value: "showroom", label: "Showroom", group: "Commercial" },
    { value: "restaurant", label: "Restaurant/Cafe", group: "Commercial" },
    { value: "hotel", label: "Hotel/Guest House", group: "Commercial" },
    { value: "plaza", label: "Commercial Plaza", group: "Commercial" },
  ];

  // Normalize the search location before passing to navigation
  const handleSearch = () => {
    const params = { 
      propertyType, 
      city, 
      location: location, // Pass original location, filtering will handle normalization
      minPrice, 
      maxPrice 
    };

    if (!propertyType && !city && !location && !minPrice && !maxPrice) {
      alert("Please select at least one search criteria");
      return;
    }

    switch(listingType) {
      case "sale":
        navigate("/category/sale", { state: params });
        break;
      case "rent":
        navigate("/category/rent", { state: params });
        break;
      default:
        navigate("/category/sale", { state: params });
    }
  };

  return (
    <div className="relative text-white flex flex-col items-center justify-center min-h-[75vh] px-4 py-8 text-center overflow-hidden">
      {/* Background with modern gradient */}
      <div className="absolute inset-0 z-0 bg-gradient-to-br from-slate-900 via-emerald-900 to-teal-800">
        {/* Animated gradient orbs */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        
        {/* Modern grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:40px_40px]"></div>
      </div>
      
      {/* Content */}
      <div className="relative z-10 w-full max-w-5xl">
        {/* Main heading */}
        <div className="mb-6">
          <h1 className="text-4xl md:text-6xl font-bold mb-3 tracking-tight">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-emerald-100 to-teal-200">
              Discover Your
            </span>
            <br />
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-emerald-300 via-teal-400 to-cyan-300">
              Perfect Property in Pakistan
            </span>
          </h1>
          
          <p className="text-lg md:text-xl text-emerald-50/90 font-light max-w-2xl mx-auto">
            Find residential and commercial properties across all major cities
          </p>
        </div>

        {/* Listing Type Buttons */}
        <div className="mb-6 flex flex-col items-center">
          <div className="flex gap-2 bg-white/10 backdrop-blur-sm p-1 rounded-full border border-white/20">
            <button
              onClick={() => setListingType("sale")}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 text-sm ${
                listingType === "sale"
                  ? "bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg"
                  : "text-emerald-100 hover:bg-white/10"
              }`}
            >
              <Building2 size={16} />
              For Sale
            </button>
            <button
              onClick={() => setListingType("rent")}
              className={`px-6 py-2 rounded-full font-semibold transition-all duration-300 flex items-center gap-2 text-sm ${
                listingType === "rent"
                  ? "bg-gradient-to-r from-cyan-500 to-teal-600 text-white shadow-lg"
                  : "text-emerald-100 hover:bg-white/10"
              }`}
            >
              <Building2 size={16} />
              For Rent
            </button>
          </div>
        </div>

        {/* Search Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-5 border border-white/20 shadow-2xl">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {/* Property Type */}
            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600 group-hover:text-emerald-500 transition-colors">
                <Building2 size={18} />
              </div>
              <select
                className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/95 text-gray-800 border border-gray-200 shadow-lg transition-all duration-300 hover:bg-white hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                value={propertyType}
                onChange={(e) => setPropertyType(e.target.value)}
              >
                <option value="">Property Type</option>
                <optgroup label="🏠 Residential">
                  {propertyTypeOptions.filter(p => p.group === "Residential").map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </optgroup>
                <optgroup label="📐 Plots & Land">
                  {propertyTypeOptions.filter(p => p.group === "Plots & Land").map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </optgroup>
                <optgroup label="🏢 Commercial">
                  {propertyTypeOptions.filter(p => p.group === "Commercial").map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>

            {/* City */}
            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600 group-hover:text-emerald-500 transition-colors">
                <MapPin size={18} />
              </div>
              <select
                className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/95 text-gray-800 border border-gray-200 shadow-lg transition-all duration-300 hover:bg-white hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              >
                <option value="">All Cities</option>
                {cities.map((cityItem) => (
                  <option key={cityItem.id} value={cityItem.name}>
                    {cityItem.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Location - Plain Text Input with hint */}
            <div className="relative group">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-emerald-600 group-hover:text-emerald-500 transition-colors">
                <Home size={18} />
              </div>
              <input
                type="text"
                placeholder="Area / Society / Landmark (e.g., B-17, DHA, Gulshan)"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="w-full pl-10 pr-3 py-3 rounded-xl bg-white/95 text-gray-800 border border-gray-200 shadow-lg transition-all duration-300 hover:bg-white hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
              />
            </div>

            {/* Search Button */}
            <button
              onClick={handleSearch}
              className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-semibold rounded-xl border border-emerald-400/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02] active:scale-95 flex items-center justify-center gap-2 text-sm"
            >
              <Search size={18} />
              Search
            </button>
          </div>

          {/* Price Range Row */}
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">PKR</span>
                <input
                  type="number"
                  placeholder={`Min Price (${listingType === "sale" ? "Sale" : "Rent"})`}
                  value={minPrice}
                  onChange={(e) => setMinPrice(e.target.value)}
                  className="w-full pl-12 pr-3 py-2.5 rounded-xl bg-white/95 text-gray-800 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                />
              </div>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">PKR</span>
                <input
                  type="number"
                  placeholder="Max Price"
                  value={maxPrice}
                  onChange={(e) => setMaxPrice(e.target.value)}
                  className="w-full pl-12 pr-3 py-2.5 rounded-xl bg-white/95 text-gray-800 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                />
              </div>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="mt-3 pt-3 border-t border-white/20">
            <div className="flex flex-wrap justify-center gap-2">
              <span className="text-xs text-emerald-100/80 font-medium mr-2">Quick:</span>
              {["Lower Portion", "Upper Portion", "Full House", "Residential Plot", "Shop"].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    const found = propertyTypeOptions.find(t => t.label === type);
                    if (found) setPropertyType(found.value);
                  }}
                  className="px-3 py-1.5 bg-white/5 hover:bg-white/15 backdrop-blur-sm rounded-full border border-white/20 text-xs transition-all duration-300 hover:scale-105"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto">
          {[
            { label: "Properties Listed", value: "2,500+" },
            { label: "Happy Clients", value: "1,200+" },
            { label: "Cities Covered", value: "15+" },
            { label: "Deals Closed", value: "850+" }
          ].map((stat, index) => (
            <div key={index} className="text-center p-3 bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 hover:bg-white/10 transition-colors">
              <div className="text-xl md:text-2xl font-bold bg-gradient-to-r from-emerald-300 to-teal-300 bg-clip-text text-transparent">
                {stat.value}
              </div>
              <div className="text-xs text-emerald-100/70 mt-1">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HeroSection;